import { RootState } from "features/store";
import api from "../api";
import { MaybePromise } from "@reduxjs/toolkit/dist/query/tsHelpers";
import { QueryReturnValue } from "@reduxjs/toolkit/dist/query/baseQueryTypes";
import {
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query";
import { User, LoginRequest, LoginResponse, CheckAuthResponse } from "../../types/interfaces";

export const apiWithAuth = api.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginRequest>({
      invalidatesTags: ["Auth"],

      query: (credentials) => ({
        url: `/auth/token/`,
        method: "POST",
        body: credentials,
      }),
    }),

    checkAuth: builder.mutation<CheckAuthResponse, void>({
      invalidatesTags: ["Auth"],
      queryFn: async (_arg, api, _extraOptions, baseQuery) => {
        const state = api.getState() as RootState;
        if (state.auth.accessToken) {
          return baseQuery({
            url: `/auth/token/verify/`,
            method: "POST",
            body: { token: localStorage.getItem("accessToken") },
          }) as MaybePromise<
            QueryReturnValue<
              CheckAuthResponse,
              FetchBaseQueryError,
              FetchBaseQueryMeta
            >
          >;
        } else {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: "Access token not found",
            },
          };
        }
      },
    }),

    logout: builder.mutation<void, void>({
      queryFn: () => ({ data: undefined }),
    }),

    getUser: builder.query<User, void>({
      providesTags: ["Auth"],
      queryFn: async (_arg, api, _extraOptions, baseQuery) => {
        if ((api.getState() as RootState).auth.isAuthenticated) {
          const result = await baseQuery(`/users/me/`);
          return result as QueryReturnValue<User, FetchBaseQueryError, FetchBaseQueryMeta>;
        } else {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: "Not authenticated",
            },
          };
        }
      },
    }),

    resetPassword: builder.mutation<
      void,
      { old_password: string; new_password: string }
    >({
      query: (passwords) => ({
        url: `/users/change-password/`,
        method: "PUT",
        body: passwords,
      }),
    }),

    refreshToken: builder.mutation<{ access: string; refresh: string }, void>({
      query: () => ({
        url: `/auth/token/refresh/`,
        method: "POST",
        body: { refresh: localStorage.getItem("refreshToken") },
      }),
    }),

    generateChatBotUrl: builder.query<any, void>({
      queryFn: (_arg, api, _extraOptions, baseQuery) => {
        if ((api.getState() as RootState).auth.isAuthenticated) {
          return baseQuery(`/web/chatbot/url/`);
        } else {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: "Not authenticated",
            },
          };
        }
      },
    }),

    forgotPassword: builder.mutation<{ message: string }, { email: string }>({
      query: (data) => ({
        url: `/auth/password-reset/`,
        method: "POST",
        body: data,
      }),
    }),

    forgotPasswordByUsername: builder.mutation<
      { status: string; statusCode: number; has_email: boolean; masked_email?: string; message: string },
      { username: string }
    >({
      query: (data) => ({
        url: `/auth/password-reset/by-username/`,
        method: "POST",
        body: data,
      }),
    }),

    validateResetToken: builder.query<{ valid: boolean; email?: string }, string>({
      query: (token) => `/auth/password-reset/validate/${token}/`,
    }),

    confirmPasswordReset: builder.mutation<
      { message: string },
      { token: string; new_password: string }
    >({
      query: (data) => ({
        url: `/auth/password-reset/confirm/`,
        method: "POST",
        body: data,
      }),
    }),

    updateEmail: builder.mutation<
      { message: string; email: string },
      { email: string }
    >({
      query: (data) => ({
        url: `/users/update-email/`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Auth"],
    }),

    requestEmailVerification: builder.mutation<
      { message: string },
      { email: string }
    >({
      query: (data) => ({
        url: `/users/verify-email/request/`,
        method: "POST",
        body: data,
      }),
    }),

    validateEmailVerificationToken: builder.query<
      { valid: boolean; email?: string },
      string
    >({
      query: (token) => `/users/verify-email/validate/${token}/`,
    }),

    confirmEmailVerification: builder.mutation<
      { message: string; email: string },
      { token: string }
    >({
      query: (data) => ({
        url: `/users/verify-email/confirm/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Auth"],
    }),
  }),
});

export const {
  useLoginMutation,
  useCheckAuthMutation,
  useLogoutMutation,
  useGetUserQuery,
  useResetPasswordMutation,
  useRefreshTokenMutation,
  useGenerateChatBotUrlQuery,
  useForgotPasswordMutation,
  useForgotPasswordByUsernameMutation,
  useValidateResetTokenQuery,
  useConfirmPasswordResetMutation,
  useUpdateEmailMutation,
  useRequestEmailVerificationMutation,
  useValidateEmailVerificationTokenQuery,
  useConfirmEmailVerificationMutation,
} = apiWithAuth;
